// apps/provider/src/services/api/webptImport.js
// Customer self-service WebPT import wizard methods
//
// Methods: reconcileWebptImportRoster, getWebptImportPatientState,
//          createWebptImportRun, updateWebptImportRun, getWebptImportRun,
//          getWebptImportRuns, importVisit, importClinicalNote, importPlanOfCare

/**
 * @returns {Object} WebPT import domain methods (bound to `this` of the composed apiService)
 */
export function createWebptImportMethods() {
  return {
    /**
     * Reconcile an entered roster against existing Prism patients.
     * Tiers per row: exact account number, ehr_id, then name+DOB.
     *
     * Xano: POST /webpt-import-reconcile
     * Xano file: webpt-import-reconcile_POST.xs
     *
     * @param {Array<{first_name: string, last_name: string, birthday: string, account_number: string, webpt_patient_id: string}>} rows
     * @returns {Promise<{results: Array<Object>}>}
     */
    async reconcileWebptImportRoster(rows) {
      return this.request('/webpt-import-reconcile', {
        method: 'POST',
        timeoutMs: 300000,
        body: JSON.stringify({ rows })
      }, 'reconcileWebptImportRoster');
    },

    /**
     * Fetch minimal-column dedup state (cases, encounters, notes, POCs, documents)
     * for a chunk of up to 25 patients.
     *
     * Xano: POST /webpt-import-patient-state
     * Xano file: webpt-import-patient-state_POST.xs
     *
     * @param {string[]} patientIds
     * @returns {Promise<{patients: Array<Object>}>}
     */
    async getWebptImportPatientState(patientIds) {
      return this.request('/webpt-import-patient-state', {
        method: 'POST',
        body: JSON.stringify({ patient_ids: patientIds })
      }, 'getWebptImportPatientState');
    },

    /**
     * Create a new import run for the caller's organization.
     *
     * Xano: POST /webpt-import-run
     * Xano file: webpt-import-run_POST.xs
     *
     * @param {string} [label]
     * @returns {Promise<Object>} The created run record
     */
    async createWebptImportRun(label) {
      return this.request('/webpt-import-run', {
        method: 'POST',
        timeoutMs: 300000,
        body: JSON.stringify({ label })
      }, 'createWebptImportRun');
    },

    /**
     * Patch an import run. Only provided fields are updated.
     *
     * Xano: PATCH /webpt-import-run/{run_id}
     * Xano file: webpt-import-run_run_id_PATCH.xs
     *
     * @param {string} runId
     * @param {{status?: string, label?: string, roster?: Object, manifest?: Object, results?: Object}} data
     * @returns {Promise<Object>} The updated run record
     */
    async updateWebptImportRun(runId, data) {
      return this.request(`/webpt-import-run/${runId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }, 'updateWebptImportRun');
    },

    /**
     * Fetch a single import run with full roster/manifest/results blobs.
     *
     * Xano: GET /webpt-import-run/{run_id}
     * Xano file: webpt-import-run_run_id_GET.xs
     *
     * @param {string} runId
     * @returns {Promise<Object>}
     */
    async getWebptImportRun(runId) {
      return this.request(`/webpt-import-run/${runId}`, {
        method: 'GET'
      }, 'getWebptImportRun');
    },

    /**
     * List the organization's import runs (no blobs).
     *
     * Xano: GET /webpt-import-runs
     * Xano file: webpt-import-runs_GET.xs
     *
     * @returns {Promise<{items: Array<Object>}>}
     */
    async getWebptImportRuns() {
      return this.request('/webpt-import-runs', {
        method: 'GET'
      }, 'getWebptImportRuns');
    },

    /**
     * Import a historical visit: creates encounter + clinical note atomically,
     * pre-signed and completed, backdated to encounter_date.
     *
     * Xano: POST /visit-import
     * Xano file: visit-import_POST.xs
     *
     * @param {Object} visitData
     * @param {string} visitData.patient_id
     * @param {string} [visitData.provider_id]
     * @param {string} visitData.location_id
     * @param {number} visitData.encounter_date - Epoch ms
     * @param {string} visitData.encounter_type
     * @param {string} visitData.note_type
     * @param {string} [visitData.visit_type]
     * @param {number} [visitData.case_id]
     * @param {string} visitData.template_id
     * @param {Object} [visitData.sections_data]
     * @param {Array} [visitData.icd10_codes]
     * @param {Array} [visitData.cpt_codes]
     * @param {string} [visitData.signed_by]
     * @param {boolean} [visitData.counts_toward_visit_total]
     * @returns {Promise<{encounter: Object, clinical_note: Object}>}
     */
    async importVisit(visitData) {
      return this.request('/visit-import', {
        method: 'POST',
        body: JSON.stringify(visitData)
      }, 'importVisit');
    },

    /**
     * Import an additional clinical note onto an existing encounter (pre-signed).
     *
     * Xano: POST /clinical-note-import
     * Xano file: clinical-note-import_POST.xs
     *
     * @param {Object} noteData
     * @param {string} noteData.encounter_id
     * @param {string} noteData.patient_id
     * @param {string} noteData.note_type
     * @param {string} noteData.template_id
     * @param {string} [noteData.provider_id]
     * @param {string} [noteData.signed_by]
     * @param {number} [noteData.case_id]
     * @param {Object} [noteData.sections_data]
     * @param {Array} [noteData.cpt_codes]
     * @returns {Promise<Object>} The created clinical note
     */
    async importClinicalNote(noteData) {
      return this.request('/clinical-note-import', {
        method: 'POST',
        body: JSON.stringify(noteData)
      }, 'importClinicalNote');
    },

    /**
     * Import a historical plan of care.
     *
     * Xano: POST /plan-of-care-import
     * Xano file: plan-of-care-import_POST.xs
     *
     * @param {Object} pocData
     * @returns {Promise<Object>} The created plan of care
     */
    async importPlanOfCare(pocData) {
      return this.request('/plan-of-care-import', {
        method: 'POST',
        body: JSON.stringify(pocData)
      }, 'importPlanOfCare');
    }
  };
}
